/**
 * THE TWO SUPPORT ROUTES, BOUND TO THE SESSION IN A LIVE BUILD - and left bare in the default one.
 *
 * WHY THIS FILE EXISTS. A live build needs two things the support screens should not do themselves,
 * because they are written to render with no router and no session at all (the injected `Navigate`
 * in features/support/navigation.ts, the optional `CabinetSessionContext`). So the binding lives
 * here, between the router and the screens:
 *
 *   1. `/support` READS THE SESSION THE PROVIDER ALREADY HOLDS. `useCabinetSession` was written
 *      waiting for this: "If the shell has put a session on `CabinetSessionContext`, USE IT AND
 *      ISSUE NOTHING" (features/support/hooks/use-cabinet-session.ts). Its own read is the same
 *      operation the provider makes - `getAuthorSession`, `GET /v1/me`
 *      (docs/design/07-api/authors-api.yaml) - and the same type in both features' aliases
 *      (`components['schemas']['AuthorSession']`), so a second read on every visit to a help page
 *      bought nothing but a request and a second answer that could disagree with the first.
 *   2. `/terms` IS THE GATE'S DETOUR. When the gate sends an author here (`termsInterstitialDue` in
 *      features/auth/session/session-state.ts), this route records that the interstitial has been
 *      shown, re-reads the session once the acceptance lands, and gives "Continue" and "Not now" the
 *      way back to the screen the gate interrupted: `next`, read through `returnPathFrom`, so only a
 *      same-origin path is ever followed (features/auth/navigation.ts, `safeReturnPath`).
 *      OPENED FROM SUPPORT there is no `next` - the gate always carries one, the support page's
 *      button never does - and "Not now" goes back to `/support` (R-044, lens U1-13): before, the
 *      page offered only "Accept", a wall with no way back but the browser's.
 *
 * ★ THE DEFAULT BUILD RENDERS THE SCREENS BARE, exactly as the router mounted them before this file.
 * The branch is `isLiveApiConfigured()`, which is decided per build, so the hook order inside each
 * branch never changes between renders - the same shape `SessionGate` uses and for the same reason
 * (features/auth/session/SessionGate.tsx).
 */
import { useCallback, useEffect } from 'react';
import { useLocation, useRouter } from '@tanstack/react-router';
import { isLiveApiConfigured } from '../../config';
import { TERMS_PATH, returnPathFrom } from '../../features/auth/navigation';
import { useAuthSession } from '../../features/auth/session/AuthSessionProvider';
import { CabinetSessionContext, SupportPage, TermsInterstitial } from '../../features/support';
import { CABINET_ROUTES as SUPPORT_PLACES } from '../../features/support/navigation';

/** `/support`. */
export function SupportPageRoute() {
  return isLiveApiConfigured() ? <LiveSupportPage /> : <SupportPage />;
}

/** `/terms`. */
export function TermsInterstitialRoute() {
  return isLiveApiConfigured() ? <LiveTermsInterstitial /> : <TermsInterstitial />;
}

function LiveSupportPage() {
  const { state } = useAuthSession();
  // Never null under the gate: `/support` renders only once a session has loaded
  // (features/auth/session/gate.ts). Were it null, the hook would fall back to its own read.
  return (
    <CabinetSessionContext.Provider value={state.session}>
      <SupportPage />
    </CabinetSessionContext.Provider>
  );
}

/** The path part of a `path?query` string. */
function pathOf(pathAndQuery: string): string {
  return pathAndQuery.split(/[?#]/, 1)[0] ?? pathAndQuery;
}

function LiveTermsInterstitial() {
  const { retryNow, noteTermsInterstitialShown } = useAuthSession();
  const router = useRouter();
  const carried = useLocation({ select: (location) => returnPathFrom(location.searchStr) });
  // A `next` that points back here would make "Not now" a button that goes nowhere.
  const next = carried !== null && pathOf(carried) !== TERMS_PATH ? carried : null;

  useEffect(() => {
    noteTermsInterstitialShown();
  }, [noteTermsInterstitialShown]);

  // A `replace`, like every move the gate makes: Back must not reopen an interstitial that is done.
  const goBack = useCallback(() => {
    router.history.replace(next ?? SUPPORT_PLACES.support);
  }, [router, next]);

  return (
    <TermsInterstitial
      onAccepted={retryNow}
      onContinue={next === null ? undefined : goBack}
      onNotNow={goBack}
    />
  );
}
